import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { boundsFromCoordinates } from '@foreflight/webmap/util/geometryBounds';

import {
    findUpdatedTracks,
    getCookieItem,
    PAGE_SIZE,
    pointsToCoordinates,
} from 'ff/debrief/utils';
import {
    Tracklog,
    AllTracklogsResponse,
    Response,
    Debrief,
    AbbrTracklog,
    DebriefStateResponse,
    ShareDetails,
    SharedWithMeResponse,
    DebriefAircraft,
    AircraftProfile,
    Vspeeds,
    TracklogLogbookAssociation,
    FFCDLogbookEntry,
    CreateLogbookStatus,
    TailNumber,
    SegmentVrefOverrideParams,
    ApproachOverrideParams,
    NavDataAirportSearchResult,
    ApproachPlan,
} from '../models';
import { RootState } from 'ff/debrief/store';
import { isFeatureEnabled } from 'ff/common/ffAppUtils';
import { LogbookEntry } from 'shared/sharedTypes/logbook/LogbookEntryModels';
import { pollForReanalysisComplete } from 'ff/debrief/features/StatusSlice';
import { TourSettings } from 'shared/sharedTypes/aircraft/aircraftV2/DTOs/TourSettings';
import { DebriefSettings } from 'shared/sharedTypes/debrief/DebriefSettings';

interface GetTracklogsArgs {
    page: number;
    uuid?: string;
    startTimestamp?: number;
    endTimestamp?: number;
    fuzzySearchTerm?: string;
}

interface VspeedOverrideParams {
    tailNumber: string;
    tracklogId: string;
    preferredSpeedUnit: string;
    overrides: Vspeeds;
}

export interface TracklogOtherInfoEditable {
    userProvidedName: string;
    tailNumber: string;
    derivedOrigin: string;
    derivedDestination: string;
    pilotName: string;
    userRemarks: string;
}

export type TracklogOtherInfoEditableUpdate =
    Partial<TracklogOtherInfoEditable>;

interface TracklogOtherInfoUpdateParams {
    objectId: string;
    tracklogId: string;
    updates: TracklogOtherInfoEditableUpdate;
}

interface AircraftListResponse {
    content: DebriefAircraft[];
}

interface importG1000Response {
    trackUuid: string;
}

interface NavDataProcedure {
    type: string;
    name?: string;
    procedure_id?: string;
    legs?: unknown[];
}

export const tracklogApi = createApi({
    reducerPath: 'tracklogApi',
    baseQuery: fetchBaseQuery({
        // Test runner will not work if we don't include full URL
        baseUrl: new URL('/', location.origin).href,
        prepareHeaders: headers => {
            headers.set('X-XSRFToken', getCookieItem('_xsrf'));
            return headers;
        },
    }),
    tagTypes: [
        'Tracklogs',
        'Debrief',
        'DebriefState',
        'Shared',
        'ShareState',
        'Vspeeds',
        'SuggestedLogbook',
        'LogbookEntries',
        'Tour',
        'Settings',
    ],
    endpoints: builder => ({
        getTracklogById: builder.query<Tracklog, string>({
            queryFn: async (trackId, api, _, baseQuery) => {
                const state = api.getState() as RootState;
                const isShareApp = state.appSlice.isShareApp;
                const url = isShareApp
                    ? `/shared/tracklogs/api/tracklogs/${trackId}`
                    : `/tracklogs/api/tracklogs/details/v2/${trackId}`;
                try {
                    const response = await baseQuery({ url: url });
                    const track =
                        (response.data as Response).track ||
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (response.data as any).tracklog;
                    const coords =
                        track.coordinates || pointsToCoordinates(track.points);
                    const bounds = boundsFromCoordinates(coords);
                    track.coordinates = coords;
                    track.viewport = { bounds: bounds };
                    track.pilotName = track.pilotName || 'Unknown';
                    track.tailNumber = track.tailNumber || 'Unknown';
                    track.timestampStart = track.timestampStart + 'Z';
                    return { data: track };
                } catch (error) {
                    return { error: error };
                }
            },
            providesTags: ['Tracklogs'],
        }),
        getAllTracklogs: builder.query<AllTracklogsResponse, GetTracklogsArgs>({
            query: ({
                page,
                uuid,
                startTimestamp,
                endTimestamp,
                fuzzySearchTerm,
            }) => {
                const queryParams = new URLSearchParams();
                queryParams.append('page', page.toString());
                queryParams.append('pageSize', PAGE_SIZE.toString());
                if (startTimestamp)
                    queryParams.append('startDate', startTimestamp.toString());
                if (endTimestamp)
                    queryParams.append('endDate', endTimestamp.toString());
                if (uuid) queryParams.append('selectedTracklogUuid', uuid);
                if (fuzzySearchTerm)
                    queryParams.append('searchTerm', fuzzySearchTerm);
                return {
                    url: `/tracklogs/api/tracklogs/?${queryParams.toString()}`,
                };
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            transformResponse: (response: any) => {
                // API returns UTC timestamps. Adding "Z" to
                // timestamp so they are parsed correctly
                const tracklogs = response.tracklogs as AbbrTracklog[];
                for (const tracklog of tracklogs) {
                    tracklog.timestampStart += 'Z';
                }
                return response;
            },
            onQueryStarted: async (
                args,
                { dispatch, getCacheEntry, queryFulfilled }
            ) => {
                const prevTracklogList = getCacheEntry()?.data?.tracklogs;
                if (!prevTracklogList) return;
                try {
                    const { data } = await queryFulfilled;
                    findUpdatedTracks(prevTracklogList, data.tracklogs).forEach(
                        track => {
                            dispatch(
                                tracklogApi.util.invalidateTags([
                                    { type: 'Debrief', id: track.trackUuid },
                                ])
                            );
                        }
                    );
                } catch (err) {
                    console.error('Failed to fetch tracklog list', err);
                }
            },
            providesTags: () => ['Tracklogs'],
        }),
        getAllSharedTracklogs: builder.query<SharedWithMeResponse, void>({
            query: () => ({ url: `/debrief/api/share/sharedWithMe` }),
            transformResponse: (response: SharedWithMeResponse) => {
                // API returns UTC timestamps. Adding "Z" to
                // timestamp so they are parsed correctly
                for (const share of response) {
                    share.tracklogSummary.timestampStart += 'Z';
                }
                return response;
            },
            providesTags: () => ['Shared'],
        }),
        getDebriefByTrackUUID: builder.query<Debrief, string>({
            queryFn: async (trackId, api, _, baseQuery) => {
                if (!isFeatureEnabled('debrief_features')) {
                    return { error: { message: 'Not enabled' } };
                }
                const state = api.getState() as RootState;
                const isShareApp = state.appSlice.isShareApp;
                const url = isShareApp
                    ? `/shared/debrief/api/${trackId}`
                    : `/debrief/api/tracklogs/${trackId}`;
                try {
                    const response = await baseQuery({ url: url });
                    if (response.data)
                        return { data: response.data as Debrief };
                    else return { error: response.error };
                } catch (error) {
                    return { error: error };
                }
            },
            providesTags: (result, error, arg) => [
                { type: 'Debrief', id: arg },
            ],
        }),
        deleteTracklog: builder.mutation({
            query: tracklogId => ({
                url: `/tracklogs/api/tracklogs/delete/byId/${tracklogId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Tracklogs'],
        }),
        triggerDebriefForTrackUUID: builder.mutation<null, string>({
            query: tracklogUuid => ({
                url: `/debrief/api/tracklogs/${tracklogUuid}`,
                method: 'POST',
            }),
            invalidatesTags: ['Debrief', 'Tracklogs', 'DebriefState'],
        }),
        getDebriefState: builder.query<DebriefStateResponse, string>({
            query: tracklogId => ({
                url: `/debrief/api/imports/${tracklogId}`,
            }),
            providesTags: ['DebriefState'],
        }),
        getShareState: builder.query<ShareDetails, string>({
            query: trackId => ({
                url: `/debrief/api/share/detailsByTrackUuid/${trackId}`,
            }),
            providesTags: (result, error, trackId) =>
                result
                    ? [
                          { type: 'ShareState', id: result?.shareId },
                          { type: 'ShareState', id: trackId },
                      ]
                    : [{ type: 'ShareState', id: trackId }],
        }),
        getShareDetails: builder.query<ShareDetails, string>({
            query: shareId => `/shared/debrief/api/share/details/${shareId}`,
            providesTags: (result, error, shareId) =>
                result
                    ? [
                          { type: 'ShareState', id: result?.shareId },
                          { type: 'ShareState', id: shareId },
                      ]
                    : [{ type: 'ShareState', id: shareId }],
        }),
        getShareDetailsByTrackUuid: builder.query<ShareDetails, string>({
            query: trackUuid => ({
                url: `/debrief/api/share/detailsByTrackUuid/${trackUuid}`,
            }),
            providesTags: (result, error, shareId) =>
                result
                    ? [
                          { type: 'ShareState', id: result?.shareId },
                          { type: 'ShareState', id: shareId },
                      ]
                    : [{ type: 'ShareState', id: shareId }],
        }),
        deleteSharedTracklog: builder.mutation({
            query: shareId => ({
                url: `/debrief/api/share/${shareId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Shared'],
        }),
        makeDebriefPublic: builder.mutation<ShareDetails, string>({
            query: trackId => ({
                method: 'POST',
                url: `/debrief/api/share/${trackId}`,
            }),
            invalidatesTags: (result, error, trackId) => [
                { type: 'ShareState', id: result.shareId },
                { type: 'ShareState', id: trackId },
            ],
        }),
        makeDebriefPrivate: builder.mutation<undefined, string>({
            query: shareId => ({
                method: 'PUT',
                url: `/debrief/api/share/unshare/${shareId}`,
            }),
            invalidatesTags: (result, error, shareId) => [
                { type: 'ShareState', id: shareId },
                'Tracklogs',
            ],
        }),
        getRecentTailNumbers: builder.query<TailNumber[], string>({
            query: tailNumber => {
                if (tailNumber) {
                    return {
                        method: 'GET',
                        url: `/debrief/api/tracklogs/aircraft/search?tailNumber=${tailNumber}`,
                    };
                } else
                    return {
                        method: 'GET',
                        url: '/debrief/api/tracklogs/aircraft/search',
                    };
            },
            transformResponse: (tails: TailNumber[]) =>
                tails.filter(t => t.tailNumber !== null),
        }),
        getAircraftList: builder.query<DebriefAircraft[], string>({
            query: search => {
                return {
                    url: `/debrief/api/aircraft-models/?query=${search}`,
                };
            },
            transformResponse: (response: AircraftListResponse) =>
                response.content.map(aircraft => ({
                    label: `${aircraft.manufacturer} / ${aircraft.model}`,
                    ...aircraft,
                })),
        }),
        saveAircraftForTailNumber: builder.mutation<
            void,
            DebriefAircraft & { tailNumber: string }
        >({
            query: ({ tailNumber, ...aircraft }) => {
                return {
                    method: 'POST',
                    url: `/debrief/api/tracklogs/aircraft/${tailNumber}/model`,
                    body: aircraft,
                };
            },
            invalidatesTags: ['Vspeeds'],
        }),
        getVSpeedOverrides: builder.query<
            AircraftProfile,
            { tailNumber: string; manufacturer?: string; model?: string }
        >({
            query: ({ tailNumber, manufacturer, model }) => {
                const query =
                    manufacturer && model
                        ? `?manufacturer=${manufacturer}&model=${model}`
                        : '';
                return {
                    method: 'GET',
                    url: `/debrief/api/tracklogs/aircraft/${tailNumber}/vspeeds${query}`,
                };
            },
            providesTags: ['Vspeeds'],
        }),
        searchNavdataAirport: builder.query<
            NavDataAirportSearchResult[],
            string
        >({
            query: term => {
                return {
                    method: 'GET',
                    url: `/navdata/api/airports?term=${term}`,
                };
            },
            transformResponse: (result: {
                results: NavDataAirportSearchResult[];
                status: string;
            }) => {
                return result?.results || [];
            },
        }),
        getNavdataAirport: builder.query<NavDataProcedure[], string>({
            query: airportId => {
                return {
                    method: 'GET',
                    url:
                        '/navdata/api/airports/' +
                        airportId +
                        '?expandedFeatures=PROCEDURES',
                };
            },
            transformResponse: (result: {
                results?: { procedures?: NavDataProcedure[] };
            }) => {
                return (
                    result?.results?.procedures?.filter(
                        (p: NavDataProcedure) => p.type === 'APPROACH'
                    ) || []
                );
            },
        }),
        getApproachPlan: builder.query<ApproachPlan, string>({
            query: approachName => {
                return {
                    method: 'GET',
                    url:
                        '/debrief/api/procedures/approachPlan?approachName=' +
                        approachName,
                };
            },
        }),
        createVspeedOverrides: builder.mutation<
            AircraftProfile,
            VspeedOverrideParams
        >({
            query: ({ tailNumber, overrides, preferredSpeedUnit }) => {
                return {
                    method: 'POST',
                    url: `/debrief/api/tracklogs/aircraft/${tailNumber}/vspeeds`,
                    body: overrides,
                    params: { preferredSpeedUnit },
                };
            },
            onQueryStarted: (arg, api) => {
                api.dispatch(pollForReanalysisComplete(arg.tracklogId));
            },
            invalidatesTags: ['Vspeeds'],
        }),
        deleteVspeedOverrides: builder.mutation<void, string>({
            query: tailNumber => {
                return {
                    method: 'DELETE',
                    url: `/debrief/api/tracklogs/aircraft/${tailNumber}/vspeeds`,
                };
            },
            invalidatesTags: ['Vspeeds'],
        }),
        updateTracklogOtherInfo: builder.mutation<
            null,
            TracklogOtherInfoUpdateParams
        >({
            query: ({ objectId, updates }) => {
                return {
                    method: 'POST',
                    url: `/tracklogs/api/tracklogs/${objectId}/otherInfo`,
                    body: updates,
                };
            },
            invalidatesTags: ['Tracklogs', 'Vspeeds'],
            onQueryStarted: (arg, api) => {
                if ('tailNumber' in arg.updates) {
                    api.dispatch(pollForReanalysisComplete(arg.tracklogId));
                }
            },
        }),
        updateSegmentVrefOverride: builder.mutation<
            null,
            SegmentVrefOverrideParams
        >({
            query: ({ tracklogId, override }) => {
                return {
                    method: 'POST',
                    url: `/debrief/api/analysis-overrides/${tracklogId}/vref`,
                    body: override,
                };
            },
            onQueryStarted: (arg, api) => {
                api.dispatch(pollForReanalysisComplete(arg.tracklogId));
            },
        }),
        deleteSegmentVrefOverride: builder.mutation<
            null,
            SegmentVrefOverrideParams
        >({
            query: ({ tracklogId, override }) => {
                return {
                    method: 'DELETE',
                    url: `/debrief/api/analysis-overrides/${tracklogId}/vref`,
                    body: override,
                };
            },
            onQueryStarted: (arg, api) => {
                api.dispatch(pollForReanalysisComplete(arg.tracklogId));
            },
        }),
        getSuggestedlog: builder.query<FFCDLogbookEntry[], string>({
            query: trackId =>
                `/tracklogs/api/${trackId}/logbook-entries/suggested`,
            transformResponse: (response: {
                suggestions: FFCDLogbookEntry[];
            }) => response.suggestions,
            providesTags: ['SuggestedLogbook'],
        }),
        getLogbookEntries: builder.query<LogbookEntry[], void>({
            query: () => `/logbook/api/entries/?drafts=false`,
            transformResponse: (response: {
                result: { content: LogbookEntry[] };
            }) => response.result.content,
            providesTags: ['LogbookEntries'],
        }),
        addLogbookObjectId: builder.mutation<
            TracklogLogbookAssociation,
            TracklogLogbookAssociation
        >({
            query: logbookEntry => ({
                url: `/tracklogs/api/tracklog/association`,
                method: 'POST',
                body: logbookEntry,
            }),
            invalidatesTags: ['Tracklogs'],
        }),
        addLogbookEntry: builder.mutation<
            CreateLogbookStatus,
            Partial<LogbookEntry>
        >({
            query: logbookEntry => ({
                url: `/logbook/api/entries`,
                method: 'POST',
                body: logbookEntry,
            }),
            invalidatesTags: [
                'LogbookEntries',
                'Tracklogs',
                'SuggestedLogbook',
            ],
        }),
        deleteLogbookEntryAssociation: builder.mutation<
            TracklogLogbookAssociation,
            TracklogLogbookAssociation
        >({
            query: logbookEntry => ({
                url: `/tracklogs/api/tracklog/association`,
                method: 'DELETE',
                body: logbookEntry,
            }),
            invalidatesTags: ['Tracklogs'],
        }),
        updateApproachOverride: builder.mutation<null, ApproachOverrideParams>({
            query: ({ tracklogId, override }) => {
                return {
                    method: 'POST',
                    url: `/debrief/api/analysis-overrides/${tracklogId}/approach`,
                    body: override,
                };
            },
            onQueryStarted: (arg, api) => {
                api.dispatch(pollForReanalysisComplete(arg.tracklogId));
            },
        }),
        deleteApproachOverride: builder.mutation<null, ApproachOverrideParams>({
            query: ({ tracklogId, override }) => {
                return {
                    method: 'DELETE',
                    url: `/debrief/api/analysis-overrides/${tracklogId}/approach`,
                    body: override,
                };
            },
        }),
        getDebriefTourSetting: builder.query<TourSettings, void>({
            query: () => ({
                url: `/debrief/api/tour`,
                method: 'GET',
            }),
            providesTags: ['Tour'],
        }),
        saveDebriefTourSetting: builder.mutation<
            void,
            TourSettings['savedTourSettings']
        >({
            query: settingsToSave => ({
                url: `/debrief/api/tour`,
                method: 'POST',
                body: settingsToSave,
            }),
        }),
        getDebriefSetting: builder.query<DebriefSettings, void>({
            query: () => ({
                url: `/debrief/api/settings`,
                method: 'GET',
            }),
            providesTags: ['Settings'],
        }),
        saveDebriefSetting: builder.mutation<
            void,
            DebriefSettings['savedDebriefSettings']
        >({
            query: settingsToSave => ({
                url: `/debrief/api/settings`,
                method: 'POST',
                body: settingsToSave,
            }),
            invalidatesTags: ['Settings'],
        }),
        importG1000Tracklog: builder.mutation<importG1000Response, FormData>({
            query: fileData => ({
                url: `/tracklogs/api/tracklogs/import`,
                method: 'POST',
                body: fileData,
            }),
            invalidatesTags: ['Tracklogs'],
        }),
    }),
});

export const {
    useGetTracklogByIdQuery,
    useGetAllTracklogsQuery,
    useGetAllSharedTracklogsQuery,
    useGetDebriefByTrackUUIDQuery,
    useDeleteTracklogMutation,
    useTriggerDebriefForTrackUUIDMutation,
    useGetDebriefStateQuery,
    useGetShareStateQuery,
    useGetShareDetailsQuery,
    useGetShareDetailsByTrackUuidQuery,
    useDeleteSharedTracklogMutation,
    useMakeDebriefPublicMutation,
    useMakeDebriefPrivateMutation,
    useGetAircraftListQuery,
    useLazyGetAircraftListQuery,
    useGetRecentTailNumbersQuery,
    useSaveAircraftForTailNumberMutation,
    useGetVSpeedOverridesQuery,
    useLazyGetVSpeedOverridesQuery,
    useCreateVspeedOverridesMutation,
    useDeleteVspeedOverridesMutation,
    useUpdateTracklogOtherInfoMutation,
    useUpdateSegmentVrefOverrideMutation,
    useDeleteSegmentVrefOverrideMutation,
    useGetSuggestedlogQuery,
    useSearchNavdataAirportQuery,
    useGetNavdataAirportQuery,
    useGetApproachPlanQuery,
    useLazySearchNavdataAirportQuery,
    useLazyGetNavdataAirportQuery,
    useGetLogbookEntriesQuery,
    useAddLogbookObjectIdMutation,
    useAddLogbookEntryMutation,
    useDeleteLogbookEntryAssociationMutation,
    useUpdateApproachOverrideMutation,
    useDeleteApproachOverrideMutation,
    useGetDebriefTourSettingQuery,
    useSaveDebriefTourSettingMutation,
    useGetDebriefSettingQuery,
    useSaveDebriefSettingMutation,
    useImportG1000TracklogMutation,
} = tracklogApi;
